import React, { ChangeEvent } from 'react';

import { useParams } from 'react-router-dom';

import { Box, Checkbox, Grid, Typography } from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ptBR } from 'date-fns/locale';
import { useFormikContext } from 'formik';

import { CissAutoComplete } from 'components/autocompletes/CissAutoComplete';
import { CissNumberField, CissTextField } from 'components/fields';
import { FullLoading } from 'components/loading';
import { useLojaColunasMenuAsideContext } from 'context/ConfiguracaoLojaColunasMenuAsideContext';
import { useListagemContext } from 'context/ListagemContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useRequestListagem, RequestOptionsType } from 'hooks/useRequestListagem';
import { LojaRequest, ColunaDinamicaProps } from 'pages/cadastro/loja/types';
import { allowNumberAndEspecialChars } from 'util/format';

const getInitialValue = (dsChave: string): boolean | string | number => {
    const typeChave = dsChave;
    switch (typeChave) {
        case 'OPCAO':
            return false;
        case 'NUMERO':
        case 'NUMERO_COM_CARACTERES_ESPECIAIS':
            return 0;

        case 'DATA':
            return String(new Date());

        case 'HORA':
            return '';

        case 'TEXTO':
            return '';
        default:
            return '';
    }
};

const parseTimeString = (timeStr: string | null | undefined) => {
    if (!timeStr) return null;

    const [hours, minutes] = timeStr.split(':');
    const now = new Date();
    now.setHours(Number(hours), Number(minutes), 0, 0);

    return now;
};

export function LojaColunasDinamicasFormGrid(): JSX.Element {
    const { values, setFieldValue, resetForm } = useFormikContext<LojaRequest>();
    const { hasPermission } = usePermissionsContext();
    const { activeStep } = useLojaColunasMenuAsideContext();
    const { id } = useParams();
    const RequestListagem = useRequestListagem();
    const { filter, page } = useListagemContext();
    const queryClient = useQueryClient();

    const requestConfigPropriedadeOptions: RequestOptionsType = {
        url: '/gestao/configuracaopropriedade/loja',
        filter: [
            ...filter,
            {
                property: 'idConfiguracaoPropriedadeGrupoExibicao',
                value: String(activeStep),
                operator: 'eq',
            },
            {
                property: 'idLoja',
                value: id,
                operator: 'eq',
            },
        ],
        sort: [{ property: 'nrSequencia', direction: 'ASC' }],
        page,
        limit: 20,
    };

    const setInitialLojaColunas = (colunas: ColunaDinamicaProps[]): void => {
        resetForm();
        const lojaColunas = colunas.map((item) => ({
            ...item,
            dsValor: item.dsValor ?? getInitialValue(item.dsChave),
        }));

        setFieldValue('lojaColunas', lojaColunas);
    };

    const { data, isFetching } = useQuery(
        ['getlojaColunas', requestConfigPropriedadeOptions],
        () => RequestListagem(requestConfigPropriedadeOptions).then((res) => res.data.data),
        {
            onSuccess: (responseColunas: ColunaDinamicaProps[]) => {
                setInitialLojaColunas(responseColunas);
            },
        },
    );

    const setLojaColunas = (idConfiguracao: number, newValue: string | null | Date | undefined) => {
        // Pega o array atual (conforme já é feito)
        const lojaColunasAtuais = values.lojaColunas || data || [];

        // Mapeia e retorna no formato desejado
        const newLojaColunas = lojaColunasAtuais.map((item) => {
            if ((item?.configuracaoPropriedade?.idConfiguracaoPropriedade || item.idConfiguracaoPropriedade) === idConfiguracao) {
                return {
                    configuracaoPropriedade: {
                        idConfiguracaoPropriedade:
                            item.configuracaoPropriedade?.idConfiguracaoPropriedade || item.idConfiguracaoPropriedade || idConfiguracao,
                    },
                    configuracaoPropriedadeOpcaoList: {
                        dsValor: newValue,
                    },
                    dsConfiguracao: item.dsConfiguracao,
                    dsValor: newValue,
                };
            }
            return {
                ...item,
                dsValor: item.dsValor,
            };
        });

        // Salva de volta no Formik
        setFieldValue('lojaColunas', newLojaColunas);
        queryClient.setQueryData(['getlojaColunas'], newLojaColunas);
    };

    const renderField = (coluna: ColunaDinamicaProps) => {
        const typeChave = coluna?.tipoConfiguracaoPropriedade?.tipoConfiguracaoPropriedadeEnum;
        const configId = coluna.idConfiguracaoPropriedade || 0;

        switch (typeChave) {
            case 'OPCAO': // "flag" -> checkbox
                return (
                    <CissAutoComplete
                        value={coluna.configuracaoPropriedadeOpcaoList?.find(
                            (opcao) => String(opcao.idConfiguracaoPropriedadeOpcao) === coluna.dsValor,
                        )}
                        options={coluna.configuracaoPropriedadeOpcaoList}
                        optionStringTemplate="{dsConfiguracaoPropriedadeOpcao}"
                        selectedStringTemplate="{dsConfiguracaoPropriedadeOpcao}"
                        label={coluna.dsConfiguracao}
                        isOptionEqualToValue={(option, value) =>
                            option && option.idConfiguracaoPropriedadeOpcao === value.idConfiguracaoPropriedadeOpcao
                        }
                        multiple={false}
                        onChange={(e, value) => {
                            if (value?.idConfiguracaoPropriedadeOpcao) {
                                setLojaColunas(configId, String(value.idConfiguracaoPropriedadeOpcao));
                                coluna.dsValor = String(value.idConfiguracaoPropriedadeOpcao);
                                return;
                            }
                            coluna.dsValor = '';
                            setLojaColunas(configId, '');
                        }}
                        disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                    />
                );

            case 'TEXTO':
                return (
                    <CissTextField
                        label={coluna.dsConfiguracao}
                        value={coluna.dsValor || ''}
                        onChange={(e) => setLojaColunas(configId, e.target.value)}
                        disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                    />
                );

            case 'NUMERO_COM_CARACTERES_ESPECIAIS':
                return (
                    <CissTextField
                        type="text"
                        label={coluna.dsConfiguracao}
                        value={coluna?.dsValor}
                        onInput={allowNumberAndEspecialChars}
                        onChange={(e) => setLojaColunas(configId, e.target.value)}
                        disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                    />
                );

            case 'NUMERO':
                return (
                    <CissNumberField
                        inputType="number"
                        label={coluna.dsConfiguracao}
                        value={coluna.dsValor || ''}
                        onChange={(e) => setLojaColunas(configId, e.target.value)}
                        disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                    />
                );

            case 'DATA':
                return (
                    <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                        <DatePicker
                            value={coluna?.dsValor}
                            label={coluna?.dsConfiguracao}
                            inputFormat="dd/MM/yyyy"
                            renderInput={(params) => <CissTextField {...params} name={coluna?.dsChave} label={coluna.dsConfiguracao} />}
                            onChange={(value) => ((coluna.dsValor = value), setLojaColunas(configId, value))}
                            disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                        />
                    </LocalizationProvider>
                );

            case 'HORA':
                return (
                    <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                        <TimePicker
                            renderInput={(params) => <CissTextField {...params} name={coluna.dsChave} label={coluna.dsConfiguracao} />}
                            value={coluna.dsValor}
                            onChange={(value) => {
                                coluna.dsValor = value;
                                setLojaColunas(configId, value);
                            }}
                            disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                        />
                    </LocalizationProvider>
                );

            case 'BOOLEANO':
                return (
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '6px', pl: '10px' }}>
                        <Typography>{coluna.dsConfiguracao}</Typography>
                        <Checkbox
                            checked={String(coluna.dsValor) === 'true'}
                            onChange={(e) => {
                                coluna.dsValor = String(e.target.checked);
                                setLojaColunas(configId, String(e.target.checked));
                            }}
                            disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                        />
                    </Box>
                );

            default:
                <CissTextField
                    value={coluna.dsValor}
                    onChange={(e) => setLojaColunas(configId, e.target.value)}
                    disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                />;
        }
    };

    if (isFetching) {
        return isFetching && <FullLoading loading={isFetching} />;
    }

    return (
        <Grid container spacing={2}>
            {data?.map((coluna) => {
                const configId = coluna?.idConfiguracaoPropriedade || 0;

                return (
                    <Grid item xs={12} sm={6} md={4} key={configId}>
                        {renderField(coluna)}
                    </Grid>
                );
            })}
        </Grid>
    );
}
